import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { notificar } from '../utils/notify';
import PlanoSaudeRegistro from '../components/PlanoSaudeRegistro';
import '../styles/painelOperador.css';
import '../styles/gestaoBpoRh.css';

// BPO RH — Plano de Saúde: todo colaborador elegível é perguntado se quer o plano e a
// resposta fica registrada com o anexo (documento de inclusão ou comprovante da recusa).
// Cada empresa tem a sua regra (na admissão ou após os 3 meses de experiência); 3 meses de
// casa sem o registro completo vira bloqueio no modal de atrasos. Backend:
// core/views_plano_saude.py
const API = process.env.REACT_APP_API_BASE;
const URL_P = `${API}/api/bporh/plano-saude/`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });

const SIT = {
  vencido: { rotulo: '3 meses sem registro', cor: '#a11d2b', bg: '#fdeaec', icone: 'fa-triangle-exclamation' },
  pendente: { rotulo: 'Aguardando resposta', cor: '#8a6d00', bg: '#fff5da', icone: 'fa-hourglass-half' },
  falta_anexo: { rotulo: 'Falta o anexo', cor: '#8a6d00', bg: '#fff5da', icone: 'fa-paperclip' },
  incluido: { rotulo: 'Quer o plano', cor: '#116b47', bg: '#e6f7ef', icone: 'fa-circle-check' },
  recusou: { rotulo: 'Não quer o plano', cor: '#116b47', bg: '#e6f7ef', icone: 'fa-circle-check' },
};

export default function BpoRhPlanoSaude() {
  const [params, setParams] = useSearchParams();
  const clienteId = params.get('cliente') || '';
  const [clientes, setClientes] = useState(null);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);
  const [aberto, setAberto] = useState(null);
  const [filtro, setFiltro] = useState('');

  const carregar = useCallback(() => {
    setCarregando(true);
    axios.get(URL_P, { params: { cliente_id: clienteId || undefined }, headers: headers() })
      .then((r) => { setClientes(r.data.clientes || []); setDados(r.data); })
      .catch((e) => setMensagem(`❌ ${e.response?.data?.mensagem || 'Não foi possível carregar'}`))
      .finally(() => setCarregando(false));
  }, [clienteId]);
  useEffect(() => { carregar(); }, [carregar]);

  const empresa = (clientes || []).find(c => String(c.id) === String(clienteId));
  const lista = useMemo(() => (dados?.colaboradores || [])
    .filter(x => !filtro || (filtro === 'abertos' ? !x.completo : x.situacao === filtro)), [dados, filtro]);
  const r = dados?.resumo || {};

  return (
    <div className="painel-op"><div className="container-fluid mt-4 shadow p-4 rounded bg-white">
      <div className="header-wrapper d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="d-flex align-items-center"><i className="fas fa-heart-pulse header-icon me-2" aria-hidden="true"></i>
          <h2 className="titulo-pagina m-0">BPO RH — Plano de Saúde</h2></div>
        <div className="gr-toolbar">
          <select className="form-select form-select-sm" style={{ width: 260 }} value={clienteId}
            onChange={(e) => setParams(e.target.value ? { cliente: e.target.value } : {})} aria-label="Empresa">
            <option value="">{clientes ? 'Escolha a empresa…' : 'Carregando…'}</option>
            {(clientes || []).map((c) => <option key={c.id} value={c.id}>{c.cliente}</option>)}
          </select>
          <button type="button" className="gr-btn" onClick={carregar} disabled={carregando}>
            <i className={`fas ${carregando ? 'fa-spinner fa-spin' : 'fa-rotate'}`} aria-hidden="true"></i>Atualizar
          </button>
        </div>
      </div>

      <div className="small text-muted mb-3">
        Todo colaborador elegível precisa ser perguntado se quer o plano de saúde. Registre a resposta e anexe o comprovante:
        o <b>documento de inclusão</b> (quer) ou a <b>comprovação de que não quer</b>. Com 3 meses de casa sem o registro
        completo, o caso entra no bloqueio de atrasos da operadora.
      </div>

      {!clienteId && <div className="text-muted small py-4 text-center">Escolha a empresa para começar.</div>}

      {clienteId && empresa && (<>
        <div className="border rounded px-3 py-2 mb-3 small" style={{ background: '#f8f9fa' }}>
          <i className="fas fa-circle-info me-1 text-muted"></i>
          Regra da <b>{empresa.cliente}</b>: adesão <b>{empresa.regra}</b>.
          {empresa.momento === 'admissao' && ' O item "Plano de Saúde" do checklist da admissão só conclui com o registro completo.'}
        </div>
        <div className="d-flex gap-2 flex-wrap mb-3">
          {[['', `Todos (${(dados?.colaboradores || []).length})`], ['abertos', `Em aberto (${(r.vencido || 0) + (r.pendente || 0) + (r.falta_anexo || 0)})`],
            ['vencido', `3 meses sem registro (${r.vencido || 0})`], ['incluido', `Quer o plano (${r.incluido || 0})`],
            ['recusou', `Não quer (${r.recusou || 0})`]].map(([k, rot]) => (
            <button key={k} type="button" className={`btn btn-sm ${filtro === k ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setFiltro(k)}>{rot}</button>
          ))}
        </div>
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr><th>Colaborador</th><th>Vínculo</th><th>Admissão</th><th>Prazo</th><th>Situação</th>
                <th>E-mail</th><th>Anexos</th><th></th></tr>
            </thead>
            <tbody>
              {lista.map((x) => {
                const s = SIT[x.situacao] || SIT.pendente;
                return (
                  <tr key={x.convenia_id}>
                    <td><div className="fw-semibold">{x.nome}</div><div className="small text-muted">{x.cargo || ''}</div></td>
                    <td className="small">{x.vinculo || '—'}</td>
                    <td>{x.admissao}</td>
                    <td>
                      {x.momento === 'admissao' ? (
                        <><span>na admissão</span><div className="small text-muted">trava o checklist</div></>
                      ) : x.limite}
                      {!x.completo && x.momento !== 'admissao' && (
                        <div className="small" style={{ color: x.dias_para_limite < 0 ? '#a11d2b' : (x.dias_para_limite <= 15 ? '#8a6d00' : '#6c757d') }}>
                          {x.dias_para_limite < 0 ? `venceu há ${-x.dias_para_limite} dia(s)` : (x.dias_para_limite === 0 ? 'vence hoje' : `em ${x.dias_para_limite} dia(s)`)}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className="badge" style={{ background: s.bg, color: s.cor, border: `1px solid ${s.cor}44` }}>
                        <i className={`fas ${s.icone} me-1`}></i>{s.rotulo}
                      </span>
                    </td>
                    <td className="small">{x.email_enviado_em || <span className="text-muted">não enviado</span>}</td>
                    <td className="small">{(x.anexos || []).length ? `${x.anexos.length} anexo(s)` : <span className="text-muted">—</span>}</td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn btn-sm btn-outline-secondary me-1"
                        onClick={() => setAberto({ ...x, _soEmail: true })}
                        title={x.email_enviado_em ? `Já enviado em ${x.email_enviado_em} — enviar de novo` : 'Perguntar ao colaborador por e-mail'}>
                        <i className="fas fa-envelope me-1"></i>Perguntar?
                      </button>
                      <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setAberto(x)}>
                        <i className="fas fa-pen-to-square me-1"></i>Registrar
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!lista.length && <tr><td colSpan={8} className="text-center text-muted py-4">Nenhum colaborador neste filtro.</td></tr>}
            </tbody>
          </table>
        </div>
      </>)}

      {aberto && (
        <PlanoSaudeRegistro clienteId={Number(clienteId)} colaborador={aberto}
          soEmail={!!aberto._soEmail}
          onFechar={() => setAberto(null)} onMudou={carregar}
          onAviso={(m) => setMensagem(`✅ ${m}`)} />
      )}
    </div></div>
  );
}
